import React, { useEffect, useRef, useState } from 'react';
import {
  completeWhatsAppConnection,
  metaRequestErrorMessage,
  type WhatsAppCompleteResult,
  type WhatsAppSignupConfig,
} from '../../services/MetaConnectionService';
import { launchEmbeddedSignup, loadFacebookSdk } from '../../services/WhatsAppEmbeddedSignup';

export interface WhatsAppSignupButtonProps {
  sectorId: number;
  config: WhatsAppSignupConfig;
  label: string;
  disabled?: boolean;
  className?: string;
  onConnected: (result: WhatsAppCompleteResult) => void;
  onError: (message: string) => void;
}

export const SIGNUP_CANCELLED_MESSAGE = 'Você fechou o cadastro do WhatsApp antes de terminar. Nada foi conectado.';
export const SIGNUP_COMPLETE_FALLBACK = 'Não foi possível concluir a conexão do WhatsApp. Tente de novo.';

/**
 * Botão do Embedded Signup do WhatsApp (Meta). Carrega o SDK ao montar para o `FB.login` abrir dentro do clique (senão o
 * navegador bloqueia o popup). Ao terminar, manda o code e o session info ao servidor, que conclui a conexão.
 */
const WhatsAppSignupButton: React.FC<WhatsAppSignupButtonProps> = ({
  sectorId,
  config,
  label,
  disabled,
  className = 'whatsapp-connect-button',
  onConnected,
  onError,
}) => {
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    // O erro de carga reaparece no clique, com a mensagem para o usuário.
    void loadFacebookSdk(config).catch(() => undefined);
    return () => {
      alive.current = false;
    };
  }, [config.appId, config.graphApiVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = () => {
    setBusy(true);
    // Sem await antes: o FB.login precisa nascer dentro do clique.
    const pending = launchEmbeddedSignup(config);
    void (async () => {
      try {
        const outcome = await pending;
        if (outcome.kind === 'cancelled') {
          onError(SIGNUP_CANCELLED_MESSAGE);
          return;
        }
        if (outcome.kind === 'error') {
          onError(outcome.errorCode ? `${outcome.message} (código ${outcome.errorCode})` : outcome.message);
          return;
        }
        const result = await completeWhatsAppConnection(sectorId, outcome.code, outcome.sessionInfo);
        onConnected(result);
      } catch (e: unknown) {
        onError(metaRequestErrorMessage(e, SIGNUP_COMPLETE_FALLBACK));
      } finally {
        if (alive.current) setBusy(false);
      }
    })();
  };

  return (
    <button type="button" className={className} disabled={disabled || busy} onClick={handleClick}>
      {busy ? 'Conectando…' : label}
    </button>
  );
};

export default WhatsAppSignupButton;
